export const getSupportedMimeType = () => {
  // Prefer MP4 on iOS/Safari (WebM decode support is inconsistent on iOS).
  // MediaRecorder.isTypeSupported will ensure we only return what the browser can produce.
  const types = ["audio/mp4", "audio/webm"];

  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  throw new Error("No supported audio MIME types found");
};

/**
 * Validates an audio blob to check if it's corrupt or invalid before sending to backend.
 * @param blob The audio blob to validate
 * @param mimeType Optional MIME type to check against
 * @returns Object with isValid flag and optional error message
 */
export async function validateAudioBlob(
  blob: Blob,
  mimeType?: string,
): Promise<{ isValid: boolean; error?: string }> {
  // Check if blob exists and has content
  if (!blob || blob.size === 0) {
    return {
      isValid: false,
      error: "Audio blob is empty",
    };
  }

  // Minimum size check: prevents obviously empty/corrupt chunks.
  // Backend also checks for tiny payloads; this just avoids unnecessary requests.
  const MIN_AUDIO_SIZE_BYTES = 500;
  if (blob.size < MIN_AUDIO_SIZE_BYTES) {
    return {
      isValid: false,
      error: `Audio blob is too small (${blob.size} bytes)`,
    };
  }

  // Validate MIME type if provided
  if (mimeType && blob.type && blob.type !== mimeType) {
    // Allow partial matches (e.g., "audio/webm;codecs=opus" matches "audio/webm")
    const baseMimeType = blob.type.split(";")[0];
    if (baseMimeType !== mimeType) {
      return {
        isValid: false,
        error: `Audio MIME type mismatch. Expected ${mimeType}, got ${blob.type}`,
      };
    }
  }

  // Do not attempt to decode client-side; rely on server-side decode validation.
  return { isValid: true };
}
